'use client';

import type { OzonetelIntegrationConfigType } from '@unifyapps/defs/types/interface';
import { useEffect } from 'react';
import useInterfaceEventBus from '../../../hooks/useInterfaceEventBus';
import type { OzonetelWindow } from './consts/ozonetel/sdk';
import { useOzonetelAgentAudioControl } from './hooks/ozonetel/useAgentAudioControl';
import { useOzonetelCallStatus } from './hooks/ozonetel/useCallStatus';
import { useOzonetelMethods } from './hooks/ozonetel/useMethods';
import { useOzonetelSdkActions } from './hooks/ozonetel/useSdkActions';
import { useOzonetelSdkLifecycle } from './hooks/ozonetel/useSdkLifecycle';
import { useOzonetelSipReadiness } from './hooks/ozonetel/useSipReadiness';
import { useOzonetelVisibilityRecovery } from './hooks/ozonetel/useVisibilityRecovery';
import { loadOzonetelSdk } from './utils/ozonetel/loadSdk';

export function OzonetelIntegration({
  ozonetelIntegrationConfig,
}: {
  ozonetelIntegrationConfig?: OzonetelIntegrationConfigType;
}) {
  const { interfaceEventBus } = useInterfaceEventBus();
  const customerId = ozonetelIntegrationConfig?.customerId.trim() ?? '';

  const { callStatusRef, isInitializedRef, setCallStatus } = useOzonetelCallStatus();
  const { markSipReady, markSipLost, assertSipReady } = useOzonetelSipReadiness();
  const {
    handleMuteAgent,
    handleUnmuteAgent,
    prepareAgentAudioForOutboundCall,
    releaseAgentAudio,
  } = useOzonetelAgentAudioControl({ callStatusRef });

  const { pendingPayloadRef, initializeOzonetel, getInitializedSdk } = useOzonetelSdkLifecycle({
    callStatusRef,
    isInitializedRef,
    setCallStatus,
    publishOztCallback: interfaceEventBus.publish,
    releaseAgentAudio,
    markSipReady,
    markSipLost,
  });

  const actions = useOzonetelSdkActions({
    callStatusRef,
    isInitializedRef,
    pendingPayloadRef,
    setCallStatus,
    getInitializedSdk,
    prepareAgentAudioForOutboundCall,
    releaseAgentAudio,
    assertSipReady,
  });

  const { handleOztMethod } = useOzonetelMethods({
    customerId,
    pendingPayloadRef,
    initializeOzonetel,
    getInitializedSdk,
    ...actions,
    handleMuteAgent,
    handleUnmuteAgent,
  });

  useOzonetelVisibilityRecovery({
    enabled: Boolean(ozonetelIntegrationConfig?.enabled && customerId),
    isInitializedRef,
    initializeOzonetel,
  });

  useEffect(() => {
    const ozonetelWindow = window as OzonetelWindow & { ozonetel?: undefined };
    ozonetelWindow.ozonetel = undefined;

    if (!ozonetelIntegrationConfig?.enabled) {
      releaseAgentAudio();
      isInitializedRef.current = false;
      setCallStatus('UNAUTHORIZED');
      return;
    }
    if (!customerId) {
      releaseAgentAudio();
      setCallStatus('UNAUTHORIZED');
      console.error('Ozonetel integration is missing customerId');
      return;
    }

    void loadOzonetelSdk().catch((error) => console.error('Failed to load Ozonetel SDK', error));
    const unsubscribe = interfaceEventBus.subscribe('onOztMethod', (payload) => {
      void handleOztMethod(payload)
        .catch((error) => console.error(`Failed to handle OZT method: ${payload.eventType}`, error))
        .finally(() => {
          console.log('OZT method handled');
        });
    });
    return () => {
      unsubscribe();
      releaseAgentAudio();
    };
  }, [
    customerId,
    handleOztMethod,
    interfaceEventBus,
    isInitializedRef,
    ozonetelIntegrationConfig?.enabled,
    releaseAgentAudio,
    setCallStatus,
  ]);

  return null;
}
